import React, { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import QueryString from 'query-string'
import Loader from '../Loader'
import EmptyState from '../EmptyState'
import API from '../../lib/api'
import { getMessage } from '../../lib/translator'
import { getOnlyDateFromUtc } from '../../lib/datetime'
import AuthenticatedPage from '../../containers/AuthenticatedPage'
import emptyIcon from '../OrderLog/order-log-empty.svg'
import LogEntry from './LogEntry'
import { extractPickingLogEntries } from './mapEntries'
import { buildDeliveryOrderDetailLinkFromContext } from '../../containers/App/fulfilmentPickingConfig'
import './style.scss'

const distinctTruthy = (values) => {
  const seen = []
  values.forEach((value) => {
    if (value && !seen.includes(value)) {
      seen.push(value)
    }
  })
  return seen
}

// Entries arrive newest-first; keep that order while bucketing into date groups.
const groupEntriesByDate = (entries) => {
  const groups = []
  const indexByDate = {}

  entries.forEach((entry) => {
    const dateLabel = entry.timestamp
      ? getOnlyDateFromUtc(entry.timestamp)
      : '—'
    if (indexByDate[dateLabel] === undefined) {
      indexByDate[dateLabel] = groups.length
      groups.push({ date: dateLabel, events: [] })
    }
    groups[indexByDate[dateLabel]].events.push(entry)
  })

  return groups
}

const buildSummary = (entries) => {
  const packlists = distinctTruthy(
    entries.map((entry) => entry.packlistReference)
  )
  const pickers = distinctTruthy(entries.map((entry) => entry.staffName))

  return [
    {
      key: 'events',
      label: getMessage('picking.logs.summary.events'),
      value: String(entries.length),
    },
    {
      key: 'packlists',
      label: getMessage('picking.logs.summary.packlists'),
      value:
        packlists.length === 0
          ? '—'
          : packlists.length <= 3
            ? packlists.join(' · ')
            : String(packlists.length),
    },
    {
      key: 'pickers',
      label: getMessage('picking.logs.summary.pickers'),
      value: String(pickers.length),
      accent: true,
    },
  ]
}

const OrderReference = ({ orderId, referenceNumber, pathname }) => {
  const displayId = referenceNumber || orderId
  if (!displayId) {
    return null
  }
  const detailPath = buildDeliveryOrderDetailLinkFromContext({
    referenceNumber,
    orderId,
    pathname,
  })

  return (
    <Link
      to={detailPath}
      className="pl-order-ref"
      data-testid="picking-log-order-link"
    >
      {/* Reuse the external-link glyph from the picking order detail page. */}
      <svg
        width="12"
        height="12"
        viewBox="0 0 24 24"
        aria-hidden="true"
        focusable="false"
      >
        <path
          fill="currentColor"
          d="M14 3h7v7h-2V6.4l-9.3 9.3-1.4-1.4L17.6 5H14V3zM5 5h6v2H7v10h10v-4h2v6H5V5z"
        />
      </svg>
      <span className="pl-order-ref-prefix">{referenceNumber ? '' : '#'}</span>
      {displayId}
    </Link>
  )
}

const PickingLog = ({ match, location, menu }) => {
  const [showLoader, setShowLoader] = useState(false)
  const [entries, setEntries] = useState([])
  const [error, setError] = useState(null)
  const apiRef = useRef(null)

  const orderId = match?.params?.orderId
  const query = QueryString.parse(location?.search || '')
  const referenceNumber = query.ref || null

  useEffect(() => {
    if (!orderId) {
      setShowLoader(false)
      setEntries([])
      setError(null)
      return undefined
    }

    let cancelled = false
    setShowLoader(true)
    setError(null)

    const api = new API({
      url: `/picking-service/v3/pickingStatusLog/${orderId}`,
    })
    apiRef.current = api

    api
      .get()
      .then((response) => {
        if (cancelled) {
          return
        }
        setEntries(extractPickingLogEntries(response))
        setShowLoader(false)
        setError(null)
      })
      .catch((err) => {
        if (cancelled) {
          return
        }
        if (err.code === 401) {
          throw err
        }
        setError(err.message)
        setShowLoader(false)
        setEntries([])
      })

    return () => {
      cancelled = true
      api.cancel()
      apiRef.current = null
    }
  }, [orderId])

  const hasEntries = entries.length > 0
  const groups = hasEntries ? groupEntriesByDate(entries) : []
  const summary = hasEntries ? buildSummary(entries) : []

  return (
    <AuthenticatedPage menu={menu} from={location?.pathname}>
      <div className="picking-log">
        <div className="pl-header">
          <h1 className="pl-title">{getMessage('picking.logs.heading')}</h1>
          <OrderReference
            orderId={orderId}
            referenceNumber={referenceNumber}
            pathname={location?.pathname}
          />
        </div>

        {error ? (
          <div className="pl-error" data-testid="picking-log-error">
            {error}
          </div>
        ) : null}

        <div data-testid="picking-log">
          {showLoader ? (
            <Loader />
          ) : hasEntries ? (
            <React.Fragment>
              <div className="pl-summary">
                {summary.map((item) => (
                  <div className="pl-summary-card" key={item.key}>
                    <div className="pl-summary-label">{item.label}</div>
                    <div
                      className={`pl-summary-value${
                        item.accent ? ' pl-summary-value--accent' : ''
                      }`}
                    >
                      {item.value}
                    </div>
                  </div>
                ))}
              </div>

              {groups.map((group) => (
                <div className="pl-group" key={group.date}>
                  <div className="pl-group-date">
                    <svg
                      width="13"
                      height="13"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                      focusable="false"
                    >
                      <path
                        fill="currentColor"
                        d="M20 3h-1V1h-2v2H7V1H5v2H4c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 18H4V8h16v13z"
                      />
                    </svg>
                    {group.date}
                  </div>
                  {group.events.map((entry) => (
                    <LogEntry key={entry.id} entry={entry} />
                  ))}
                </div>
              ))}

              <div className="pl-timeline-end">
                {getMessage('picking.logs.timelineStart')}
              </div>
            </React.Fragment>
          ) : (
            <EmptyState
              icon={emptyIcon}
              message={getMessage('picking.logs.emptyMessage')}
            />
          )}
        </div>
      </div>
    </AuthenticatedPage>
  )
}

export default PickingLog
